import { useFormField, useDirtyState } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Modal } from '@calendly/ui/components/modal';
import { Body, Header, Footer, Title, Popup } from '@calendly/ui/components/popup';
import { Sheet } from '@calendly/ui/components/sheet';
import { Table, Header as TableHeader, HeaderCell, Body as TableBody } from '@calendly/ui/components/table';
import classNames from 'classnames';
import { useMemo, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { isScimConfigurationSavingSelector, newUserGroupsSelector, userGroupsSelector, previewResponseSelector, attributeValuesSelector } from '../selectors';
import { StatusBadge } from './styles';
import { groupAndAggregate } from './utils';
const popupContainerIsApplyingToExistingCss = "py7j5lo";
const popupContainerCss = "phvamba";
const PopupContainer = forwardRef((props, ref) => {
  const {
    applyToExisting = false
  } = props;
  const isApplyingToExisting = applyToExisting;
  return <Sheet ref={ref} {...props} className={classNames(popupContainerCss, {
    [popupContainerIsApplyingToExistingCss]: isApplyingToExisting
  }, props.className)} />;
});
const styledTableCss = "s1lvgb5n";
const StyledTable = forwardRef((props, ref) => <Table ref={ref} {...props} className={classNames(styledTableCss, props.className)} />);
const styledRowCss = "syk4vzb";
const StyledRow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'tr';
  return <Component ref={ref} {...restProps} className={classNames(styledRowCss, props.className)} />;
});
const styledCellCss = "sob6vzc";
const StyledCell = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'td';
  return <Component ref={ref} {...restProps} className={classNames(styledCellCss, props.className)} />;
});
const tableCaptionCss = "t1rquykf";
const TableCaption = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'caption';
  return <Component ref={ref} {...restProps} className={classNames(tableCaptionCss, props.className)} />;
});
const redStrikeThroughCss = "rhlmmyk";
const RedStrikeThrough = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(redStrikeThroughCss, props.className)} />;
});
const grayTextCss = "gfalgr2";
const GrayText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(grayTextCss, props.className)} />;
});
const DeletedItemGroupName = ({
  defaultGroup
}) => defaultGroup ? defaultGroup.name : <GrayText>(none)</GrayText>;
const DeletedAttribute = value => <div key={`key-${value}`}>
    <RedStrikeThrough>{value}</RedStrikeThrough>
    {' '}
    <StatusBadge>Deleted</StatusBadge>
  </div>;
const GroupNameContents = ({
  groupName,
  isNewGroup,
  applyToExisting,
  attrValues,
  defaultGroup
}) => {
  if (isNewGroup) {
    return <>
        {groupName}
        {' '}
        <StatusBadge>{I18n.t('publisher.organization_settings.user_provisioning.groups.calendly_group.new_label')}</StatusBadge>
      </>;
  }
  const allDestroyed = attrValues.every(attrValue => attrValue.isBeingDeleted === true);
  if (allDestroyed) {
    if (applyToExisting) {
      return <DeletedItemGroupName defaultGroup={defaultGroup} />;
    }
    return <RedStrikeThrough>{groupName}</RedStrikeThrough>;
  }
  return <div>{groupName}</div>;
};
const NoChangesPopup = ({
  isOpen,
  onClose
}) => <Popup isOpen={isOpen} onClose={onClose} title={I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.title_no_changes')}>
    <Body>
      {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.there_needs_to_be_at_least')}
    </Body>
    <Footer>
      <Button onClick={onClose}>{I18n.t('publisher.action.go_back')}</Button>
    </Footer>
  </Popup>;
const MapAttributes = ({
  attrValues
}) => attrValues.map(attrValue => attrValue.isBeingDeleted ? DeletedAttribute(attrValue.value) : <div key={`key-${attrValue.id}`}>
        {attrValue.value}
        {attrValue.isNewAttributeValue ? <>
            {' '}
            <StatusBadge>{I18n.t('publisher.organization_settings.user_provisioning.groups.calendly_group.new_label')}</StatusBadge>
          </> : null}
      </div>);
const PreviewItemRow = ({
  defaultGroup,
  applyToExisting,
  item
}) => {
  const {
    attrValues,
    groupName,
    isNewGroup,
    countOfGroupMembersBefore = 0,
    countOfGroupMembersAfter = 0
  } = item;
  return <StyledRow>
      <StyledCell>
        <MapAttributes attrValues={attrValues} />
      </StyledCell>
      <StyledCell>
        <GroupNameContents applyToExisting={applyToExisting} attrValues={attrValues} defaultGroup={defaultGroup} groupName={groupName} isNewGroup={isNewGroup} />
      </StyledCell>
      {applyToExisting && <>
            <StyledCell>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.x_users', {
          count: countOfGroupMembersBefore
        })}
            </StyledCell>
            <StyledCell>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.x_users', {
          count: countOfGroupMembersAfter
        })}
            </StyledCell>
          </>}
    </StyledRow>;
};
export const PreviewChangesModal = ({
  isOpen,
  onClose,
  onConfirm,
  settingsNewlyToggledOn
}) => {
  const isDirty = useDirtyState();
  const isSaving = useSelector(isScimConfigurationSavingSelector);
  const userGroups = useSelector(userGroupsSelector);
  const newGroups = useSelector(newUserGroupsSelector);
  const previewResponse = useSelector(previewResponseSelector);
  const attributeValues = useSelector(attributeValuesSelector);
  const [{
    value: mappings
  }] = useFormField({
    name: 'mappings'
  });
  const [{
    value: defaultGroupId
  }] = useFormField({
    name: 'defaultUserGroupId'
  });
  const [{
    value: applyToExisting
  }] = useFormField({
    name: 'applyToExisting'
  });
  const mappingsToPreview = useMemo(() => mappings.filter(({
    attrValue,
    groupId
  }) => Boolean(attrValue) || Boolean(groupId)).map(({
    id: mappingId,
    key,
    attrValue,
    groupId,
    isBeingDeleted
  }) => {
    const group = newGroups.find(({
      id
    }) => id === groupId) || userGroups.find(({
      id
    }) => id === groupId);
    const attributeValue = attributeValues.find(attrValueItem => attrValueItem.name === attrValue);
    const groupName = group?.isNewGroup ? group?.originalName : group?.name;
    const groupSize = applyToExisting ? previewResponse.find(({
      attribute_value: previewAttrValue
    }) => attrValue === previewAttrValue) : {};
    return {
      attrValue,
      isBeingDeleted,
      key: mappingId || key,
      groupName,
      isNewGroup: !!group?.isNewGroup,
      isNewAttributeValue: !!attributeValue?.isNew,
      ...groupSize
    };
  }), [applyToExisting, attributeValues, mappings, newGroups, previewResponse, userGroups]);
  const mappingToDelete = mappings.some(mapping => mapping.isBeingDeleted);
  const defaultGroup = newGroups.find(({
    id
  }) => id === defaultGroupId) || userGroups.find(({
    id
  }) => id === defaultGroupId);
  const defaultGroupPreviewItem = useMemo(() => {
    const defaultGroupSize = applyToExisting ? previewResponse.find(({
      attribute_value: previewAttrValue
    }) => previewAttrValue === null) : {};
    const groupName = defaultGroup?.isNewGroup ? defaultGroup?.originalName : defaultGroup?.name;
    return {
      ...defaultGroupSize,
      key: 'defaultGroup',
      attrValue: <>
          <GrayText>(none)</GrayText>
          {' '}
          <StatusBadge>Default</StatusBadge>
        </>,
      isNewGroup: !!defaultGroup?.isNewGroup,
      groupName
    };
  }, [applyToExisting, defaultGroup, previewResponse]);
  const previewItems = useMemo(() => groupAndAggregate([...(defaultGroup ? [defaultGroupPreviewItem] : []), ...mappingsToPreview]), [defaultGroup, defaultGroupPreviewItem, mappingsToPreview]);
  if (!isDirty && !mappingToDelete && !settingsNewlyToggledOn) {
    return <NoChangesPopup isOpen={isOpen} onClose={onClose} />;
  }
  return <Modal isOpen={isOpen} onClose={onClose}>
      <PopupContainer applyToExisting={applyToExisting}>
        <Header>
          <Title>{I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.title')}</Title>
        </Header>
        <Body>
          <StyledTable>
            <TableCaption>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.verify_your_changes')}
              {applyToExisting && <strong>
                      {' '}
                      {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.note_that_once_you_save')}
                    </strong>}
            </TableCaption>
            <TableHeader>
              <HeaderCell>
                {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.identity_provider_value')}
              </HeaderCell>
              <HeaderCell>
                {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.calendly_group')}
              </HeaderCell>
              {applyToExisting && <>
                    <HeaderCell>
                      {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.group_size_before')}
                    </HeaderCell>
                    <HeaderCell>
                      {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.group_size_after')}
                    </HeaderCell>
                  </>}
            </TableHeader>
            <TableBody>
              {previewItems.map(previewItem => <PreviewItemRow key={previewItem.key} item={previewItem} applyToExisting={applyToExisting} defaultGroup={defaultGroup} />)}
            </TableBody>
          </StyledTable>
        </Body>
        <Footer>
          <Button decoration="secondary-outline" onClick={onClose}>
            {I18n.t('publisher.action.go_back')}
          </Button>
          <Button loading={isSaving} onClick={onConfirm}>
            {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_modal.save_and_continue')}
          </Button>
        </Footer>
      </PopupContainer>
    </Modal>;
};

require("./preview_changes.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./preview_changes.js");